import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { contractPdf } from "@/lib/contractPdf";
import { STATUS } from "@/components/BookingCard";
import { Download, FileText, ArrowLeft } from "lucide-react";

const STATUS_FILTERS = ["pending_artist", "confirmed", "rejected", "expired"];

export default function ContractArchive() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [year, setYear] = useState("all");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    if (!user) return;
    api.get("/bookings").then((r) => setItems(r.data.filter((b) => b.clientSignature)));
  }, [user]);

  const years = useMemo(() => [...new Set((items || []).map((b) => b.eventDate.slice(0, 4)))].sort().reverse(), [items]);
  const shown = (items || []).filter((b) => (year === "all" || b.eventDate.startsWith(year)) && (status === "all" || b.status === status));

  if (!user) return <div className="max-w-3xl mx-auto px-6 py-16 text-center"><p>Devi accedere.</p><Link to="/login" className="btn-pill-gold inline-flex mt-4">Accedi</Link></div>;
  const isProvider = ["artist", "supplier"].includes(user.role);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="contract-archive-page">
      <Link to="/dashboard" className="text-sm text-slate-500 hover:text-navy inline-flex items-center gap-1 mb-4"><ArrowLeft className="w-4 h-4"/>Dashboard</Link>
      <span className="overline text-gold block">Archivio</span>
      <h1 className="font-display text-4xl sm:text-5xl font-bold mt-2 mb-8">I tuoi contratti</h1>

      <div className="card-premium p-4 flex flex-wrap gap-3 items-center mb-6">
        <select data-testid="archive-year" value={year} onChange={(e) => setYear(e.target.value)} className="border border-slate-300 rounded-xl px-3 py-2 text-sm">
          <option value="all">Tutti gli anni</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select data-testid="archive-status" value={status} onChange={(e) => setStatus(e.target.value)} className="border border-slate-300 rounded-xl px-3 py-2 text-sm">
          <option value="all">Tutti gli stati</option>
          {STATUS_FILTERS.map((s) => <option key={s} value={s}>{STATUS[s][0]}</option>)}
        </select>
        <span className="text-sm text-slate-500 ml-auto" data-testid="archive-count">{shown.length} contratti</span>
      </div>

      {items === null && <p className="text-sm text-slate-500">Caricamento…</p>}
      {items && shown.length === 0 && <div className="card-premium p-10 text-center text-slate-500 text-sm" data-testid="archive-empty">Nessun contratto per i filtri scelti.</div>}

      <div className="space-y-3">
        {shown.map((b) => (
          <div key={b.id} className="card-premium p-4 flex items-center gap-4 flex-wrap" data-testid={`archive-row-${b.id}`}>
            <FileText className="w-8 h-8 text-gold shrink-0"/>
            <div className="min-w-[200px] flex-1">
              <div className="font-semibold truncate">{isProvider ? b.clientName : b.artistName}</div>
              <div className="text-xs text-slate-500">{b.eventDate} · {b.eventLocation} · {b.eventType}</div>
            </div>
            <span className={STATUS[b.status]?.[1] || "chip-navy"}>{STATUS[b.status]?.[0] || b.status}</span>
            {b.totalGross !== undefined && <span className="font-display font-bold text-gold w-24 text-right">€{b.totalGross.toFixed(2)}</span>}
            <button data-testid={`archive-download-${b.id}`} onClick={() => contractPdf(b)} className="btn-pill-gold text-xs"><Download className="w-3.5 h-3.5"/>Scarica PDF</button>
          </div>
        ))}
      </div>
    </div>
  );
}
